/* eslint-disable no-prototype-builtins */
import { distanceFromBottom } from './utils';
import Logger from './logger';
import ObserverIntersection, {
  DEFAULT_THRESHOLD,
  resolveThreshold,
  isSmartLazyLoadingEnabled,
} from './observer_intersection';
import Events, { EventTypes } from './events';
import Filters from '../filters/ad_insertion';
import SlotWrapper from './slot_wrapper';
import { DisplayGateKeys } from './display_gate_keys';

let uniqueId = 0;

export default class Slot {
  /**
   * Create new Slot
   * @param {object} data
   */
  constructor(app = {}, data = {}, neighbor) {
    this.app = app;
    this.data = data;
    this.neighbor = neighbor;

    this.configName = generateName.call(this);
    this.name = generateInstanceName.call(this);
    this.timesRendered = 0;

    this.applyDefaults();

    // We also want to store the ID. Generally, we have just the name of the slot,
    // but in case we want to change that, we can.
    this.id = this.data.id ? this.data.id : this.name;
  }

  /**
   * We take anything passed on as an extra option, which could include things
   * like this slot is not prebid eligible, and add it into the data we want
   * to store in the recorded slots.
   * @return {object} Official slot object
   */
  applyDefaults({ refresh = false, state = {} } = {}) {
    // Mark keys to exclude from data
    const exclude = ['name'];

    // These defaults live directly on the Slot object
    let slotDefaults = {
      index: 0,
      context: {},
    };

    if (!refresh) {
      slotDefaults.element = false;
    }

    for (var def in slotDefaults) {
      if (slotDefaults.hasOwnProperty(def)) this[def] = slotDefaults[def];
    }

    for (var d in this.data) {
      if (exclude.indexOf(d) > -1) continue;
      if (this.data.hasOwnProperty(d)) this[d] = this.data[d];
    }

    // Normalize the display-gate exemption list: the gate keys this slot bypasses in render() (see
    // ConcertAds#gatesBlocking). Always a fresh array so considerFirstSlot can push onto it without
    // mutating the shared config; a single key may be given as a bare string.
    if (Array.isArray(this.exemptFromBlocking)) {
      this.exemptFromBlocking = [...this.exemptFromBlocking];
    } else if (typeof this.exemptFromBlocking === 'string' && this.exemptFromBlocking) {
      this.exemptFromBlocking = [this.exemptFromBlocking];
    } else {
      this.exemptFromBlocking = [];
    }

    // The `firstSlotResponse` gate is opt-in: only slots configured `waitForFirstSlotResponse: true`
    // wait on it, so every other slot is exempt from it. The special slots — out-of-page and adhesion —
    // default to waiting whenever `waitForFirstSlot` is on (a skin/wallpaper or sticky bar should not
    // request ahead of the first slot's response); an explicit `waitForFirstSlotResponse` of either
    // value overrides that default. Derived from config here (not pushed by the caller) so it also
    // holds after refresh(), which re-runs this method. Every flag here is read by truthiness, the
    // same rule install() and the special-slot sweep use, so a `1` or `'true'` from a remote config
    // behaves the same everywhere.
    const isSpecialSlot = Boolean(this.data.outOfPage) || Boolean(this.data.adhesion);
    const waitsForFirstSlotResponse = Boolean(
      this.data.waitForFirstSlotResponse ?? (isSpecialSlot && Boolean(this.app.settings?.waitForFirstSlot))
    );

    if (!waitsForFirstSlotResponse && !this.exemptFromBlocking.includes(DisplayGateKeys.FIRST_SLOT_RESPONSE)) {
      this.exemptFromBlocking.push(DisplayGateKeys.FIRST_SLOT_RESPONSE);
    }

    // Now, we add in the attributes that under no circumstances do we want to
    // be able to override as we register this slot.
    //
    // `eager: true` is the readable positive alias for "do not lazy-load" — it forces
    // watcherEligible false so the slot skips the viewport IntersectionObserver and becomes
    // display-eligible immediately (see isEagerEligible / GamAdSlot#canDisplayImmediately). It is
    // the inverse of the double-negative `watcherEligible: false`; either form works, and eager wins
    // if both are given. NOTE: eager only bypasses the lazy-load observer, NOT the display gates —
    // an eager slot's render() still awaits any gate it is not exempt from (the separate
    // exemptFromBlocking axis).
    let defaultState = {
      collapsed: false,
      defined: false,
      displayEligible: false,
      watcherEligible: this.data.watcherEligible === false || this.data.eager === true ? false : true,
      displayed: false,
      renderTime: false,
      firstSlot: false,
      // `deferDisplay: true` parks the slot until an explicit display() call (see isDeferDisplay).
      deferDisplay: this.data.deferDisplay === true,
    };

    this._state = typeof state === 'object' ? { ...state, ...defaultState } : defaultState;

    return this;
  }

  /**
   * Determine whether this slot can be displayed on the page
   * @return {boolean}
   */
  canBeDisplayed() {
    // Ad-suppression gate (app.disableAds). The slot-to-type mapping lives in adsDisabledFor();
    // here we just ask whether this slot is disabled. Guards `inserted()`, so this covers
    // install-time slots, registerSlot, reinstall, and observe re-arms in one place.
    if (this.app.adsDisabledFor?.(this)) {
      Logger.log('Not available for display: ads disabled');
      return false;
    }

    var context = this.context || {};
    var browserWidthForSlot = context.browser_width || {};

    // If the requested context device type is not null— and the device type
    // requested by the ad is not what is— then we will not show the ad.
    if (context.device_type && context.device_type.indexOf(this.app.variables.device_type) < 0) {
      Logger.log('Not available for display: device');
      return false;
    }

    // More complex. If the requested browser width minimum is greater than the
    // current with (smaller than what we want) OR if we have a requested max
    // width (not zero) and our max width is less than the current browser
    // width, we do not show the ad.
    if (
      browserWidthForSlot.min > window.innerWidth ||
      (browserWidthForSlot.max !== 0 && browserWidthForSlot.max < window.innerWidth)
    ) {
      Logger.log('Not available for display: browser width');
      return false;
    }

    // Check to see if the element in question exists, and if
    // it does not, we skip it entirely.
    // First check in app.dom (which may be scoped), then fall back to document
    // This allows parent ads to refresh even when app.dom is scoped to a container
    if (this.app.dom) {
      const elementInAppDom = this.app.dom.querySelector(`#${this.id}`);
      const elementInDocument = document.querySelector(`#${this.id}`);

      if (!elementInAppDom && !elementInDocument) {
        Logger.log('Not available for display: element');
        return false;
      }
    }

    if (this.app.settings.isPreview) {
      Logger.log('Not available for display: preview');
      return false;
    }

    return true;
  }

  /**
   * Destroy this slot's wrapper and DOM element, and remove it from DFP.
   */
  destroy() {
    this.wrapper.destroy();
    Logger.log(`Slot ${this.name} was destroyed`);
  }

  /**
   * Method that runs once the slot is inserted into the DOM
   * @return {undefined}
   */
  inserted() {
    if (!this.canBeDisplayed()) return;

    const elem = this.getElement();

    if (elem) {
      elem.dispatchEvent(
        new CustomEvent('concertAdRegistered', { detail: { slotElement: elem }, bubbles: true, composed: true })
      );
    }

    Events.emit(EventTypes.slotInserted, { slotName: this.name });

    this.setDefined();

    // deferDisplay: park until display(). Undo setDefined()'s displayEligible so no render() fires.
    if (this.isDeferDisplay()) {
      this._state.displayEligible = false;
      Logger.log(`${this.name} deferred (deferDisplay); awaiting display()`);
      return;
    }

    this.isEager() ? this.render() : this.observe();
  }

  /**
   * Callback run when the observer marks the slot as visible.
   * @return {undefined}
   */
  observed() {
    Events.emit(EventTypes.slotObserved, { slotName: this.name });

    this.render();
    Logger.log(`${this.name} scrolling into view and displaying`);
    this._state.displayEligible = false;
  }

  /**
   * Callback run when slot is rendered
   * @return {undefined}
   */
  rendered() {
    const elem = this.getElement();
    this._state.rendered = true;
    const renderLocation = distanceFromBottom(elem);

    this._state.renderTime = Date.now() - this._state.renderTime;

    Events.emit(EventTypes.adRendered, {
      slotId: this.id,
      slotName: this.name,
      renderLocation,
    });
    elem.dispatchEvent(
      new CustomEvent('concertAdRendered', { detail: { slotElement: elem }, bubbles: true, composed: true })
    );
    Logger.log(`Rendering slot: ${this.name}`);
  }

  /**
   * Callback run when the slot is starting to be collapsed
   * @return {undefined}
   */
  collapsed() {
    this._state.collapsed = true;
    Events.emit(EventTypes.slotCollapsed, { slotName: this.name });
  }

  /**
   * Whether this slot is eligible for eager loading — it requests (and bids) immediately rather
   * than waiting on its IntersectionObserver. True for the designated first slot or a slot configured
   * non-watched (watcherEligible:false). The first slot is eager-eligible while still staying
   * watcher-eligible, so it does NOT trip the collapse guard (`!isWatcherEligible()`) in
   * GamAdSlot#publishToDfp. Shared by isEager() (render path), BidManager#addBid (bid path), and the
   * special-slot sweep, so all agree on what makes a slot eager.
   * @return {Boolean}
   */
  isEagerEligible() {
    return this.isFirstSlot() || !this.isWatcherEligible();
  }

  /**
   * Should slot be eager loaded now? Eager-eligible AND ready to render (defined, not yet displayed).
   * @return {Boolean}
   */
  isEager() {
    return this.isEagerEligible() && this.isDisplayEligible() && !this.isDisplayed();
  }

  /**
   * Does this slot have the given size? Most sizes are a [w, h] array
   * but we have at least one size that is a string, the `fluid` size.
   * @param  {Array or String}  size
   * @return {Boolean}
   */
  hasSize(size) {
    return this.sizes.some(s => {
      return Array.isArray(s) && Array.isArray(size)
        ? s
            .slice()
            .sort()
            .join(',') ===
            size
              .slice()
              .sort()
              .join(',')
        : s === size;
    });
  }

  /**
   * Get the slot element by its ID. If element is not cached, finds and caches element.
   * @return {HTML element}
   */
  getElement() {
    if (!this.element) {
      this.element = document.getElementById(this.id);
    }
    return this.element;
  }

  /**
   * Add this slot to the Observer, which will trigger the #observed callback
   * on this slot when observed.
   */
  observe() {
    const target = document.getElementById(this.id);
    if (!target) {
      Logger.log(`Could not watch ${this.name} because it is not in the DOM`);
      return;
    }

    // Determine threshold based on smartLazyLoad settings
    let threshold;
    const settings = this.app.settings || {};
    const useSmartLazyLoading = isSmartLazyLoadingEnabled(settings);

    if (useSmartLazyLoading) {
      // Use smart threshold resolution (section + device + platform)
      threshold = resolveThreshold(this, target);
    } else {
      // Legacy: use explicit threshold or fallback to default
      threshold = this.data.observerThreshold || settings.observerThreshold || DEFAULT_THRESHOLD;
    }

    ObserverIntersection.watchElement(target, threshold, () => this.observed(), {
      settings: this.app.settings,
    });
    Logger.log(`Starting to watch ${this.name} with threshold: ${threshold}`);
  }

  isCollapsed() {
    return this._state.collapsed;
  }

  isDefined() {
    return this._state.defined;
  }

  isDisplayEligible() {
    return this._state.displayEligible;
  }

  isDisplayed() {
    return this._state.displayed;
  }

  /**
   * Whether this slot's ad response has rendered (slotRenderEnded, empty responses included). Like the
   * rest of the slot state it is rebuilt by a refresh, so it describes the current request cycle.
   * @return {boolean}
   */
  isRendered() {
    return Boolean(this._state.rendered);
  }

  isWatcherEligible() {
    return this._state.watcherEligible;
  }

  isFirstSlot() {
    return this._state.firstSlot;
  }

  /**
   * Whether this slot waits for an explicit {@link Slot#display} call instead of rendering eagerly or
   * on its observer. Set by config `deferDisplay: true`; reset from config on refresh.
   * @return {boolean}
   */
  isDeferDisplay() {
    return this._state.deferDisplay === true;
  }

  /**
   * Whether this is the page adhesion (sticky bottom) slot. Its render() awaits the
   * lazily-loaded adhesion plugin chunk (see GamAdSlot#render), since the controller must
   * bind the slot into its bar before it requests.
   * @return {boolean}
   */
  isAdhesion() {
    return this.adhesion === true;
  }

  /**
   * Whether this slot's render() waits on the `firstSlotResponse` gate. Driven by the exemption list
   * (see applyDefaults), so an explicit `exemptFromBlocking` override and the first slot's own
   * exemption are both honored.
   *
   * A slot parked with `deferDisplay` never waits, whatever its exemptions say: it does not render
   * until display(), and display() forces the request past every gate. Counting it would let, say, an
   * out-of-page interstitial (auto-flagged to wait) arm, restart or replace a gate that nothing is
   * actually held on.
   * @return {boolean}
   */
  waitsOnFirstSlotResponse() {
    return !this.isDeferDisplay() && !this.exemptFromBlocking.includes(DisplayGateKeys.FIRST_SLOT_RESPONSE);
  }

  /**
   * Called by render() the moment this slot's ad request fires. For the designated first slot that
   * (1) releases the `firstSlot` gate, then (2) re-anchors the `firstSlotResponse` countdown to NOW, so
   * `firstSlotResponseTimeout` budgets only GAM's answer and not the registration-to-request gap (the
   * bid auction). Unlike the release, the re-anchor does not depend on `autoReleaseFirstSlot`.
   * No-op for any other slot.
   */
  firstSlotRequested() {
    this.releaseFirstSlotHold();
    if (this.isFirstSlot()) {
      this.app.restartFirstSlotResponseTimer('first slot requested');
    }
  }

  /**
   * Open the `firstSlot` display gate, releasing every slot held behind it. Called once the
   * designated first slot has fired its ad request, or from a bail path (cannot display /
   * destroyed) where it never will — both mean the gate no longer needs to wait on this slot.
   * No-op for a slot that is not the first slot, when the feature is off, or when the page owns the
   * release (`settings.autoReleaseFirstSlot === false`). See {@link ConcertAds#releaseDisplay}.
   */
  releaseFirstSlotHold() {
    if (this.isFirstSlot() && this.app.settings.autoReleaseFirstSlot !== false) {
      this.app.releaseDisplay(DisplayGateKeys.FIRST_SLOT, 'firstSlotRequested');
    }
  }

  /**
   * Open the `firstSlotResponse` display gate, releasing every slot that opted in with
   * `waitForFirstSlotResponse`. Called when the designated first slot's GAM response renders
   * (GamAdSlot#rendered), or from a bail path (cannot display / destroyed) where no response will
   * ever come. No-op for a slot that is not the first slot. See {@link ConcertAds#releaseDisplay}.
   *
   * Unlike {@link Slot#releaseFirstSlotHold} this ignores `settings.autoReleaseFirstSlot`: that setting
   * hands the `firstSlot` gate to an external controller, while this gate has no external owner.
   */
  releaseFirstSlotResponseHold() {
    if (this.isFirstSlot()) {
      this.app.releaseDisplay(DisplayGateKeys.FIRST_SLOT_RESPONSE, 'firstSlotResponded');
    }
  }

  /**
   * Release both first-slot gates. For the paths where the first slot will never request or render —
   * destroyed, or unable to display — so nothing held behind either gate waits on it. The normal
   * request and render paths release one gate each (releaseFirstSlotHold / releaseFirstSlotResponseHold)
   * at their own point in the lifecycle.
   */
  releaseFirstSlotGates() {
    this.releaseFirstSlotHold();
    this.releaseFirstSlotResponseHold();
  }

  /**
   * Whether destroy() has run. Lets work queued before the teardown (a gate-held render) skip a slot
   * that no longer exists.
   * @return {boolean}
   */
  isDestroyed() {
    return this._state.destroyed === true;
  }

  /**
   * Whether this slot type participates in the display gates. Only slot types whose render()
   * awaits `beforeAdsRequested` (and therefore can await the active gate promises and release the
   * firstSlot gate) should return true. GAM only — see GamAdSlot.
   * @return {boolean}
   */
  supportsDisplayGates() {
    return false;
  }

  shouldTrackRenderedEvent() {
    return false;
  }

  shouldTrackScrollVelocity() {
    return false;
  }

  /**
   * Set a slot as defined within DFP. While this is only called internally in this
   * class, it is also used during tests.
   *
   * @return {undefined}
   */
  setDefined() {
    this._state.displayEligible = true;
    this._state.defined = true;
  }

  /**
   * Mark this slot as displayed. Called from Ad.
   * @return {undefined}
   */
  markAsDisplayed() {
    this._state.displayed = true;
  }

  /**
   * Show content in this slot
   * @return {undefined}
   */
  render() {
    // Defense-in-depth for the ad-suppression gate (app.disableAds): a slot already observing when
    // ads are disabled must not fetch when its deferred render() finally fires. adsDisabledFor()
    // owns the slot-to-type mapping.
    if (this.app.adsDisabledFor?.(this)) return false;

    return this.isDisplayEligible() && !this.isDisplayed();
  }

  /**
   * Force this slot to request now, bypassing every display gate — the external trigger for a
   * `deferDisplay` slot, e.g. `app.getSlotById(divId).display()`. Idempotent once displayed.
   *
   * Passes awaitParkedBid: unlike the out-of-page sweep, this call is an explicit, well-timed
   * trigger, so a slot whose bid is still parked behind the lazy-bid observer should auction before
   * it renders rather than fire unbidded. The gate bypass survives that wait — see
   * GamAdSlot#queueUntilAfterBid.
   *
   * @return {boolean} true if requested now; false if a no-op or deferred until bidding.
   */
  display() {
    if (this.isDisplayed()) return false;
    this._state.deferDisplay = false;
    this._state.displayEligible = true;
    Logger.log(`${this.name} display() called; requesting now (bypassing display gates)`);
    return this.requestOrQueueForBid(true, true);
  }

  /**
   * Request the ad now, or defer it until header bidding completes. Base slots have no header
   * bidding, so they always request immediately. GamAdSlot overrides this to hold the request
   * until bidded() when a bid is in flight — rendering mid-bid would fire the ad without Prebid
   * targeting. Shared by observed() and the display-gate out-of-page sweep.
   *
   * @param  {boolean} [force=false]  Passed through to render() (GAM: bypass the display-gate wait).
   *   Also marks the request as out-of-band (not observer-driven) — see the GAM override.
   * Callers may pass a second `awaitParkedBid` argument (see {@link GamAdSlot#requestOrQueueForBid});
   * it is meaningless here, since base slots have no header bidding, so this signature ignores it.
   *
   * @return {boolean}                true if the ad was requested now; false if deferred until bidding.
   */
  requestOrQueueForBid(force = false) {
    this.render(force);
    return true;
  }

  /**
   * Get the holdSize for a slot, falling back to preview if given
   * @return {mixed} Array [100,100] or null
   */
  getHoldSize() {
    if (this.holdSize) {
      return this.holdSize;
    }

    if (this.app.settings.isPreview && this.previewHoldSize) {
      return this.previewHoldSize;
    }

    return null;
  }

  /**
   * Get slot wrapper
   * @param  {Slot}     slot       Instance of Slot
   * @param  {object}   config     Config
   * @param  {boolean}  existing   Whether we're creating from an existing DOM el
   * @return {SlotWrapper}         SlotWrapper instance
   */
  getSlotWrapper({ slot, config, existing }) {
    return new SlotWrapper({ slot, config, existing });
  }

  /**
   * Determine if a slot will pass a filter it has specified (if any)
   * @param  {object} params              Parameters object
   * @param  {object} params.config       Slot config
   * @param  {Node}   params.el           Neighbor element
   * @param  {object} params.settings     App settings
   * @param  {Document} params.scopedDom  DOM scope to search within
   * @return {Boolean}                    True if passes, false if not
   */
  static filterSlot({ config, el, settings, scopedDom }) {
    if (!config.filters) {
      return true;
    }

    var filterName, filterArg, filter;
    var filters = Object.keys(config.filters);
    for (var i = 0; i < filters.length; i++) {
      filterName = filters[i];
      filterArg = config.filters[filterName];
      filter = Filters[filterName];

      if (!filter) {
        continue;
      }

      // Pass scopedDom through the filterArg options object
      // For arrays, add scopedDom as a property on the array itself to preserve Array.isArray()
      let filterOptions;
      if (Array.isArray(filterArg)) {
        filterOptions = [...filterArg];
        filterOptions.scopedDom = scopedDom;
      } else if (typeof filterArg === 'object' && filterArg !== null) {
        filterOptions = { ...filterArg, scopedDom };
      } else {
        filterOptions = filterArg;
      }

      if (!filter(el, filterOptions, settings, config)) {
        Logger.log(`${config.name} didn't pass filter: ${filterName}`);
        return false;
      }
    }

    return true;
  }

  static shouldSkip() {
    return false;
  }
}

/**
 * Generate a new slot name
 * @return {string}
 */
function generateName() {
  return this.data.name && this.data.name.length ? this.data.name : generateDynamicName();
}

/**
 * Create the instance name of the slot, incremented with an integer if it's
 * one of many of the same type.
 *
 * Since we key by name (and DFP does as well) we need to ensure that the
 * name is actually unique. We also use the name as-is for the
 * configuration of other things, such as the Prebid data. To ensure we do
 * not duplicate any slots, we will add some logic that will test for the name.
 * @return {string}
 */
function generateInstanceName() {
  if (!this.app.slotNames.exists(this.configName)) {
    this.app.slotNames.increment(this.configName);
    return this.configName;
  }

  let slotName = this.configName;
  let count = this.app.slotNames.getCount(this.configName);
  slotName = `${slotName}_${count}`;

  this.app.slotNames.increment(this.configName);

  return slotName;
}

/**
 * Generates a dynamic name for slots that don't have a name specified
 * @return {string}
 */
function generateDynamicName() {
  return `v-dynamic-slot--${++uniqueId}`;
}
